import { useEffect, useRef, useState, useCallback } from 'react'
import { useCallStore } from '../store/call'
import { hangup, toggleMute } from '../lib/sip'
import { PhoneOff, Mic, MicOff, Minus, Maximize2, Phone } from 'lucide-react'
import { cn } from '../lib/utils'
import { useAuthStore } from '../store/auth'

// Format seconds → mm:ss
function fmt(sec: number) {
  const m = Math.floor(sec / 60).toString().padStart(2, '0')
  const s = (sec % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

export function CallOverlay() {
  const { state, leadName, leadPhone, isMuted, durationSeconds, isMinimized, toggleMinimize } = useCallStore()
  const { user } = useAuthStore()
  const canSeePhone = user?.role !== 'sales_user' || !!user?.canSeePhone

  // ── Dragging ────────────────────────────────────────────────────────────
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const posRef = useRef(pos)
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0 })

  // Set initial position once window is available
  useEffect(() => {
    const x = window.innerWidth - 360
    const y = window.innerHeight - 260
    setPos({ x, y })
    posRef.current = { x, y }
  }, [])

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragState.current.dragging) return
    const x = dragState.current.originX + (e.clientX - dragState.current.startX)
    const y = dragState.current.originY + (e.clientY - dragState.current.startY)
    const clamped = {
      x: Math.max(0, Math.min(x, window.innerWidth - 340)),
      y: Math.max(0, Math.min(y, window.innerHeight - 60)),
    }
    posRef.current = clamped
    setPos(clamped)
  }, [])

  const onMouseUp = useCallback(() => {
    dragState.current.dragging = false
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }, [onMouseMove])

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: posRef.current.x,
      originY: posRef.current.y,
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [onMouseMove, onMouseUp])

  // Cleanup listeners on unmount
  useEffect(() => () => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }, [onMouseMove, onMouseUp])

  // ── Don't render when idle ───────────────────────────────────────────────
  if (state === 'idle') return null

  const isActive   = state === 'in_call'
  const isRinging  = state === 'ringing'
  const isEnded    = state === 'ended'
  const isConnecting = state === 'connecting'

  // ── Minimized bar ────────────────────────────────────────────────────────
  if (isMinimized) {
    return (
      <div
        style={{ left: pos.x, top: pos.y }}
        className="fixed z-50 flex items-center gap-3 bg-gray-900 text-white px-4 py-2.5 rounded-full shadow-2xl cursor-default select-none"
        onMouseDown={onDragStart}
      >
        <span className={cn(
          'w-2 h-2 rounded-full flex-shrink-0',
          isActive ? 'bg-green-400 animate-pulse' : isRinging ? 'bg-amber-400 animate-pulse' : 'bg-gray-500',
        )} />
        <span className="text-sm font-medium max-w-[120px] truncate">{leadName}</span>
        {isActive && <span className="text-xs text-green-400 tabular-nums">{fmt(durationSeconds)}</span>}
        {isRinging && <span className="text-xs text-amber-400">Ringing…</span>}
        {isEnded && <span className="text-xs text-gray-400">Ended</span>}

        {!isEnded && (
          <button
            onMouseDown={e => e.stopPropagation()}
            onClick={hangup}
            className="w-6 h-6 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
            title="End call"
          >
            <PhoneOff className="w-3 h-3" />
          </button>
        )}

        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={toggleMinimize}
          className="text-gray-400 hover:text-white transition-colors"
          title="Expand"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
    )
  }

  // ── Full overlay ─────────────────────────────────────────────────────────
  return (
    <div
      style={{ left: pos.x, top: pos.y, width: 320 }}
      className="fixed z-50 bg-gray-900 rounded-2xl shadow-2xl overflow-hidden select-none"
    >
      {/* Header / drag handle */}
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-gray-800 cursor-grab active:cursor-grabbing"
        onMouseDown={onDragStart}
      >
        <div className="flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-400 font-medium tracking-wide uppercase">
            {isConnecting ? 'Connecting' : isRinging ? 'Outgoing' : isActive ? 'On Call' : 'Call Ended'}
          </span>
        </div>
        <button
          onMouseDown={e => e.stopPropagation()}
          onClick={toggleMinimize}
          className="text-gray-500 hover:text-gray-300 transition-colors p-0.5"
          title="Minimize"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-5 flex flex-col items-center gap-4">
        {/* Avatar circle */}
        <div className={cn(
          'w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white',
          isActive ? 'bg-green-700' : isRinging ? 'bg-amber-700' : 'bg-gray-700',
        )}>
          {leadName ? leadName.charAt(0).toUpperCase() : '?'}
        </div>

        {/* Name & phone */}
        <div className="text-center">
          <p className="text-white font-semibold text-base leading-tight">{leadName ?? 'Unknown'}</p>
          {canSeePhone && <p className="text-gray-400 text-sm mt-0.5">{leadPhone ?? '—'}</p>}
        </div>

        {/* Status */}
        <div className="flex items-center gap-2">
          {isConnecting && (
            <span className="text-gray-400 text-sm">Connecting…</span>
          )}
          {isRinging && (
            <>
              <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
              <span className="text-amber-400 text-sm">Ringing…</span>
            </>
          )}
          {isActive && (
            <>
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-green-400 text-sm font-mono tabular-nums">
                {fmt(durationSeconds)}
              </span>
            </>
          )}
          {isEnded && (
            <span className="text-gray-500 text-sm">Call ended</span>
          )}
        </div>

        {/* Action buttons */}
        {!isEnded && (
          <div className="flex items-center gap-6 mt-1">
            {/* Mute */}
            <button
              onClick={toggleMute}
              disabled={!isActive}
              className={cn(
                'flex flex-col items-center gap-1.5 transition-opacity',
                !isActive && 'opacity-40 cursor-default',
              )}
            >
              <div className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center transition-colors',
                isMuted ? 'bg-red-600 hover:bg-red-500' : 'bg-gray-700 hover:bg-gray-600',
              )}>
                {isMuted
                  ? <MicOff className="w-5 h-5 text-white" />
                  : <Mic className="w-5 h-5 text-white" />
                }
              </div>
              <span className="text-xs text-gray-400">{isMuted ? 'Unmute' : 'Mute'}</span>
            </button>

            {/* End call */}
            <button
              onClick={hangup}
              className="flex flex-col items-center gap-1.5"
            >
              <div className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center transition-colors">
                <PhoneOff className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs text-gray-400">End</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
